/**
 * ManualImportTicket - Admin control to queue a task for a Jira ticket on
 * demand, regardless of whether a task or PR already exists for it
 */

import { useState, type FormEvent } from 'react';
import { Loader2, Download } from 'lucide-react';

interface ManualImportTicketProps {
  onImport: (ticketKey: string) => Promise<boolean>;
  isSubmitting: boolean;
}

const TICKET_KEY_PATTERN = /^[A-Z][A-Z0-9]*-\d+$/;

export function ManualImportTicket({ onImport, isSubmitting }: ManualImportTicketProps) {
  const [ticketKey, setTicketKey] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = ticketKey.trim().toUpperCase();

    if (!TICKET_KEY_PATTERN.test(normalized)) {
      setValidationError('Ticket key must look like PROJ-123');
      return;
    }

    setValidationError('');
    const succeeded = await onImport(normalized);
    if (succeeded) {
      setTicketKey('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-start gap-2">
      <div>
        <input
          type="text"
          placeholder="Import ticket (e.g. PROJ-123)"
          value={ticketKey}
          onChange={(e) => {
            setTicketKey(e.target.value);
            if (validationError) setValidationError('');
          }}
          disabled={isSubmitting}
          className="dark:text-white px-3 py-2 w-56 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
        />
        {validationError && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationError}</p>
        )}
      </div>
      <button
        type="submit"
        disabled={isSubmitting || ticketKey.trim().length === 0}
        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
      >
        {isSubmitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        Import
      </button>
    </form>
  );
}
